import { Progress } from "antd";
import Typography from "antd/es/typography";
import Konva from "konva";
import { useEffect, useMemo, useRef, useState } from "react";
import { Arc } from "react-konva";
import { shallowEqual, useSelector } from "react-redux";
const { Text } = Typography;

export function Spinner({ tip }: { tip?: string }) {
    const [isSmall, setIsSmall] = useState<boolean>(false);

    const message = useSelector((state: any) => state.setLoading.message);

    const { fileList, loading, pendingUploads, uploadProgress } = useSelector(
        (state: any) => ({
            fileList: state.mediaUpload?.fileList || [],
            loading: Boolean(state.mediaUpload?.loading),
            pendingUploads: Number(state.mediaUpload?.pendingUploads || 0),
            uploadProgress: state.mediaUpload?.uploadProgress || {},
        }),
        shallowEqual,
    );
    const albumUploadByTemplate = useSelector(
        (state: any) => state.albumUpload?.byTemplate ?? {},
        shallowEqual,
    );
    const uploadSummary = useMemo(() => {
        const progressValues = Object.values(uploadProgress || {})
            .filter((value): value is number => typeof value === "number")
            .map((value) => Math.max(0, Math.min(100, value)));

        const albumProgressValues = Object.values(albumUploadByTemplate)
            .flatMap((t: any) => Object.values(t?.uploadProgress ?? {}))
            .filter((v): v is number => typeof v === "number")
            .map((v) => Math.max(0, Math.min(100, v)));

        const albumUploading = Object.values(albumUploadByTemplate).some(
            (t: any) => t?.uploading === true,
        );

        const allProgressValues = [...progressValues, ...albumProgressValues];
        const averageProgress = allProgressValues.length
            ? Math.round(
                  allProgressValues.reduce((sum, value) => sum + value, 0) /
                      allProgressValues.length,
              )
            : 0;

        return {
            isUploading: loading || pendingUploads > 0 || albumUploading,
            totalFiles: fileList.length || allProgressValues.length,
            averageProgress,
        };
    }, [fileList, loading, pendingUploads, uploadProgress, albumUploadByTemplate]);
    useEffect(() => {
        const onResize = () => setIsSmall(window.innerWidth < 600);
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    const uploadLabel = uploadSummary.totalFiles > 0 ? "Uploading" : "Uploading files";
    return (
        <div className="fixed inset-0 z-9999 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="flex gap-0.5 items-center h-5">
                {[0, 0.1, 0.2, 0.3].map((d, i) => (
                    <span
                        key={i}
                        className="w-0.5 h-full bg-(--primary-600) rounded-sm animate-bounce"
                        style={{ animationDelay: `${d}s` }}
                    />
                ))}
            </div>
            {message && <p className="mt-4 text-[12px] text-gray-400 tracking-wide">{message}</p>}
            {tip && <p className="mt-4 text-[12px] text-gray-400 tracking-wide">{tip}</p>}
            {uploadSummary.isUploading && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: isSmall ? "6px 10px" : "6px 12px",
                        borderRadius: 999,
                        minWidth: isSmall ? undefined : 200,
                    }}
                    aria-live="polite"
                >
                    {!isSmall && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 0,
                            }}
                        >
                             <p className="mt-4 text-[12px] text-center text-gray-400 tracking-wide">{uploadLabel}</p>
        
                            <Progress
                                percent={uploadSummary.averageProgress}
                                size="small"
                                status="active"
                                strokeColor="#4f46e5"
                                style={{ width: 150, margin: 0 }}
                            />
                        </div>
                    )}
                    {isSmall && (
                        <Text strong style={{ fontSize: 12, color: "#4338ca" }}>
                            {uploadSummary.averageProgress}%
                        </Text>
                    )}
                </div>
            )}
        </div>
    );
}

export const KonvaSpinner = ({ width, height }: { width: number; height: number }) => {
    const arcRef = useRef<Konva.Arc>(null);

    useEffect(() => {
        const anim = new Konva.Animation(() => {
            if (arcRef.current) {
                arcRef.current.rotate(6);
            }
        }, arcRef.current?.getLayer());

        anim.start();

        return () => anim.stop();
    }, []);

    return (
        <Arc
            ref={arcRef}
            x={width / 2}
            y={height / 2}
            innerRadius={10}
            outerRadius={14}
            angle={270}
            rotation={0}
            stroke="#6366f1"
            strokeWidth={3}
        />
    );
};
